import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { Languages, LogOut, Palette } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Dialog } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../app/i18n';
import { useSession } from '../../auth/session';
import { useTheme } from '../../theme/ThemeProvider';
import { THEME_CHOICES } from '../../theme/ThemeSwitcher';
import { NAV } from './AppShell';

// ⌘K command palette (§17a): jump anywhere, switch theme or language, sign out — keyboard first.

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const { signOut } = useSession();

  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount aria-describedby={undefined}>
              <motion.div
                className="fixed left-1/2 top-[14vh] z-50 border border-border bg-surface/95 backdrop-blur-xl w-[min(620px,calc(100vw-24px))] -translate-x-1/2 overflow-hidden rounded-xl elevated"
                initial={{ opacity: 0, scale: 0.96, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: -6 }}
                transition={{ type: 'spring', stiffness: 460, damping: 34 }}
              >
                <Dialog.Title className="sr-only">{t('shell.palette.open')}</Dialog.Title>
                <Command loop className="flex flex-col">
                  <Command.Input
                    placeholder={t('shell.palette.placeholder')}
                    className="h-14 border-b border-border bg-transparent px-5 text-md text-text outline-none placeholder:text-muted"
                  />
                  <Command.List className="max-h-[52vh] overflow-y-auto p-2">
                    <Command.Empty className="px-4 py-8 text-center text-muted">
                      {t('shell.palette.empty')}
                    </Command.Empty>
                    <Group heading={t('shell.palette.navigate')}>
                      {NAV.filter((n) => !n.soon).map((n) => (
                        <Item
                          key={n.to}
                          icon={n.icon}
                          onSelect={() => run(() => void navigate({ to: n.to }))}
                        >
                          {t(n.labelKey)}
                        </Item>
                      ))}
                    </Group>
                    <Group heading={t('shell.palette.appearance')}>
                      {THEME_CHOICES.map((id) => (
                        <Item
                          key={id}
                          icon={<Palette size={16} />}
                          onSelect={() => run(() => setTheme(id))}
                        >
                          {t(id === 'system' ? 'theme.system.name' : `theme.${id}.name`)}
                        </Item>
                      ))}
                    </Group>
                    <Group heading={t('shell.palette.language')}>
                      <Item
                        icon={<Languages size={16} />}
                        onSelect={() => run(() => setLanguage('en-CA'))}
                      >
                        {t('language.en')}
                      </Item>
                      <Item
                        icon={<Languages size={16} />}
                        onSelect={() => run(() => setLanguage('fr-CA'))}
                      >
                        {t('language.fr')}
                      </Item>
                    </Group>
                    <Group heading={t('shell.palette.account')}>
                      <Item
                        icon={<LogOut size={16} />}
                        onSelect={() =>
                          run(() => void signOut().then(() => navigate({ to: '/sign-in' })))
                        }
                      >
                        {t('shell.user.signOut')}
                      </Item>
                    </Group>
                  </Command.List>
                </Command>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted"
    >
      {children}
    </Command.Group>
  );
}

function Item({
  icon,
  children,
  onSelect,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-base text-text data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
    >
      <span className="text-muted">{icon}</span>
      {children}
    </Command.Item>
  );
}
